import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useEffect, useRef } from 'react';
import { apiClientV2 } from '~/api/client';
import { CreatorGender } from '~/clientApi/creator.model';
import { queryClient } from '~/config/reactQueryConfig';
import {
  AGENCY_MANAGEMENT_CREATORS_COLLECTION_QUERY_KEY,
  ALL_TALENT_ROSTER_QUERY_KEY,
  CREATOR_PROFILE_QUERY_KEY,
  GLOBAL_SEARCH_TALENT_QUERY_KEY,
  MY_TALENT_ROSTER_QUERY_KEY,
  SAVED_LIST_CREATORS,
} from '~/queries/keys';
import { useEditCreatorStore } from '~/screens/MyAgency/store';
import { COMPARATOR_TYPES, Filter, searchCreatorApi } from '~/searchApi';
import { ICreator, ICreatorLocation } from '~/types';

type CreatorPayload = {
  igHandle?: string;
  ttHandle?: string;
  snapHandle?: string;
  ytHandle?: string;
  id?: string;
  firstName?: string;
  lastName?: string;
  location?: ICreatorLocation;
  gender?: CreatorGender | null;
  dateOfBirth?: string | null;
  biography?: string;
  category?: string;
  updatedAt?: string;
};

type ReqPayload = {
  id: string;
  updatedAt: string;
  type: 'json' | 'formdata';
  creator: CreatorPayload | FormData;
};

type UpdateTalentResponse = {
  success: boolean;
  data: ICreator;
};

export const useUpdateTalent = () => {
  const nowDate = new Date().toISOString();
  const creatorLastUpdatedDateRef = useRef(nowDate);
  const { isEditModalOpen } = useEditCreatorStore();
  const isEditModalOpenRef = useRef(isEditModalOpen);

  // we use this effect update the modal state using ref to avoid stale value
  useEffect(() => {
    isEditModalOpenRef.current = isEditModalOpen;
  }, [isEditModalOpen]);

  const pollForCreatorUpdated = async (creatorId: string) => {
    // criteria to filter the creator
    // 1 - creator id
    // 2 - creator after update 'updated_at' > last stored 'updated_at'
    const filters: Filter[] = [
      {
        name: 'id',
        comparator: COMPARATOR_TYPES.EQ,
        value: creatorId,
      },
      {
        name: 'updated_at',
        comparator: COMPARATOR_TYPES.GT,
        value: creatorLastUpdatedDateRef.current,
      },
    ];

    const delay = (ms: number) =>
      new Promise((resolve) => setTimeout(resolve, ms));

    const checkUpdate = async (): Promise<boolean> => {
      // if the modal is closed, stop the polling
      if (!isEditModalOpenRef.current) {
        return false;
      }

      const response = await searchCreatorApi.getCreators({ filters });
      // if not meet the filter, then wait for 2 seconds and check again
      if (response.results.length === 0) {
        await delay(2000);
        return checkUpdate();
      }

      // if the creator is updated and returned, then return true
      return true;
    };

    return checkUpdate();
  };

  return useMutation<UpdateTalentResponse, AxiosError, ReqPayload>({
    mutationFn: async ({ id, updatedAt, creator }: ReqPayload) => {
      creatorLastUpdatedDateRef.current = updatedAt;
      const response = await apiClientV2.post(
        `/creators/${id}/platforms`,
        creator,
      );
      return response.data;
    },
    onSuccess: async (updatedCreator) => {
      // start polling for the creator
      await pollForCreatorUpdated(updatedCreator.data.id);
      // invalidate the queries once the creator is updated
      queryClient.invalidateQueries({
        queryKey: [ALL_TALENT_ROSTER_QUERY_KEY],
      });
      queryClient.invalidateQueries({ queryKey: [MY_TALENT_ROSTER_QUERY_KEY] });
      queryClient.invalidateQueries({
        queryKey: [GLOBAL_SEARCH_TALENT_QUERY_KEY],
      });
      queryClient.invalidateQueries({ queryKey: [CREATOR_PROFILE_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [SAVED_LIST_CREATORS] });
      queryClient.invalidateQueries({
        queryKey: [AGENCY_MANAGEMENT_CREATORS_COLLECTION_QUERY_KEY],
      });
    },
  });
};
